<template>
  <div v-if="showVendorsMap" v-cloak class="vd-VendorsMap_Wrapper">
    <div class="vd-VendorsMap_LeftRow">
      <div class="px-3 pt-3">
        <button class="btn btn-sm btn-secondary-subtle mb-2" @click="hideVendorsMap">
          <i class="fa fa-arrow-left pr-2"></i>
          <span>{{ backLinkText }}</span>
        </button>
        <div class="input-group shadow-sm mb-2">
          <input
            v-model.trim="query"
            class="form-control border-0"
            :placeholder="inputPlaceholder"
            autocomplete="off"
          />
          <div class="input-group-append">
            <button class="btn btn-primary" type="submit">
              <i class="icon icon-search i-lg"></i>
            </button>
          </div>
        </div>
      </div>
      <div ref="overflowbox" class="vd-VendorsMap_OverflowList">
        <div class="px-3 pb-3">
          <div v-if="!filteredVendors.length">{{ notFoundText }}</div>
          <transition-group name="list" tag="div">
            <StoreVendorsMapTile
              v-for="vendor of filteredVendors"
              :ref="`tile-${vendor.id}`"
              :key="vendor.id"
              :vendor="vendor"
              :link-text="linkText"
              :active="activeVendor.id === undefined || vendor.id === activeVendor.id"
              :show-link="vendor.id === activeVendor.id"
              class="list-item vd-VendorsMap_VendorTile bg-white p-2 mb-2 rounded"
              @select-vendor="selectVendor"
            />
          </transition-group>
        </div>
      </div>
    </div>
    <div class="vd-VendorsMap_RightRow">
      <div class="vd-VendorsMap_DeliveryZoneHeader">{{ deliveryZoneHeader }}</div>
      <GoogleMap ref="mapRef" :zoom="zoom" :center="center" @map-loaded="afterLoadMap">
        <template slot-scope="{ map }">
          <GoogleMapMarker
            v-for="vendor in mapData"
            :key="vendor.id"
            :ref="`marker-${vendor.id}`"
            :icon="{
              url: vendor.id === activeVendor.id ? activeIcon : vendor.icon,
              anchor: new google.maps.Point(50, 65),
            }"
            :title="vendor.name"
            :google="google"
            :map="map"
            :position="{ lat: vendor.lat, lng: vendor.lng }"
            :clickable="true"
            :z-index="vendor.id === activeVendor.id ? 100 : 0"
            @click="selectVendor(vendor)"
          />
          <GoogleMapPolygon
            v-for="(path, index) in paths"
            :key="index"
            :path="path"
            :google="google"
            :map="map"
          />
          <GoogleMapInfoWindow
            v-if="infoWinOpen"
            :google="google"
            :map="map"
            :position="infoWindowPos"
            :options="infoOptions"
            :opened="infoWinOpen"
            @closeclick="infoWinOpen = false"
          />
        </template>
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import GoogleMap from "../google-map/StoreGoogleMap";
import GoogleMapMarker from "../google-map/GoogleMapMarker";
import GoogleMapPolygon from "../google-map/GoogleMapPolygon";
import GoogleMapInfoWindow from "../google-map/GoogleMapInfoWindow";
import StoreVendorsMapTile from "./StoreVendorsMapTile";

export default {
  name: "StoreVendorsMap",
  components: {
    GoogleMap,
    GoogleMapMarker,
    GoogleMapInfoWindow,
    GoogleMapPolygon,
    StoreVendorsMapTile,
  },
  props: {
    mapData: {
      type: Array,
      required: true,
    },
    cityZone: {
      type: Object,
      required: true,
    },
    inputPlaceholder: {
      type: String,
      required: true,
    },
    linkText: {
      type: String,
      required: true,
    },
    backLinkText: {
      type: String,
      required: true,
    },
    notFoundText: {
      type: String,
      required: true,
    },
    deliveryZoneHeader: {
      type: String,
      required: true,
    },
    activeIcon: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      paths: [],
      center: {
        lat: this.mapData[0].lat,
        lng: this.mapData[0].lng,
      },
      zoom: 14,
      query: "",
      infoOptions: {
        content: "",
        pixelOffset: {
          width: -3,
          height: -50,
        },
      },
      iconOffset: undefined,
      infoWindowPos: null,
      infoWinOpen: false,
      google: null,
    };
  },
  computed: {
    ...mapGetters({
      showVendorsMap: "vendorsIndex/showVendorsMap",
      activeVendor: "vendorsIndex/activeVendor",
    }),
    filteredVendors() {
      if (this.query.length === 0) return this.mapData;
      this.clearActiveVendor();
      return this.filterList(this.query, this.mapData);
    },
  },
  watch: {
    activeVendor(newVal) {
      const selectedElement = this.$refs[`tile-${newVal.id}`];
      if (!selectedElement) return;
      this.toggleInfoWindow(newVal);
      selectedElement[0].$el.scrollIntoView({ behavior: "smooth" });
    },
  },
  methods: {
    ...mapActions({
      setVendorsMapVisibility: "vendorsIndex/setVendorsMapVisibility",
      setActiveVendor: "vendorsIndex/setActiveVendor",
    }),
    afterLoadMap() {
      this.google = this.$refs.mapRef.google;
      this.createDeliveryAreas();
    },
    toggleInfoWindow(vendor) {
      this.infoWindowPos = {
        lat: vendor.lat,
        lng: vendor.lng,
      };
      this.infoOptions.content = `
        <div>
          <p class="h6 mb-2">${vendor.name}</p>
          <div>
            <a href="${vendor.path}" class="badge badge-pill badge-gray-300 text-dark mb-0 h6">
              ${this.linkText}
              <span class="fa fa-caret-right"></span>
            </a>
          </div>
        </div>`;
      this.infoWinOpen = true;
    },
    createDeliveryAreas() {
      if (!this.cityZone) {
        return;
      }
      this.bounds = new this.google.maps.LatLngBounds();
      if (this.cityZone.delivery_areas) {
        this.paths = this.cityZone.delivery_areas.map(deliveryArea => {
          let mappedArea = deliveryArea[0].map(points => {
            let latlng = {
              lat: points[1],
              lng: points[0],
            };
            this.bounds.extend(latlng);
            return latlng;
          });
          return mappedArea;
        });
      }
      this.mapData.map(vendor => {
        this.bounds.extend({
          lat: vendor.lat,
          lng: vendor.lng,
        });
      });
      this.$refs.mapRef.map.fitBounds(this.bounds);
    },
    clearActiveVendor() {
      this.setActiveVendor({ id: undefined });
    },
    filterList(q, list) {
      return list.filter(item => {
        return item.name.toLowerCase().indexOf(q.toLowerCase()) > -1;
      });
    },
    hideVendorsMap() {
      this.setVendorsMapVisibility(false);
    },
    selectVendor(vendor) {
      this.setActiveVendor(vendor);
      this.toggleInfoWindow(vendor);
      this.zoom = 18;
      this.$refs.mapRef.map.panTo({ lat: vendor.lat, lng: vendor.lng });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/global";

.vd-VendorsMap_Wrapper {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: $gray-100;
  z-index: $zi-higher;
  display: flex;
}

.vd-VendorsMap_LeftRow {
  width: 320px;
  display: flex;
  flex-direction: column;
}

.vd-VendorsMap_RightRow {
  flex-grow: 1;
  height: 100%;
  display: flex;
  position: relative;
}

.vd-VendorsMap_OverflowList {
  overflow-y: auto;
}

.vd-VendorsMap_DeliveryZoneHeader {
  @include header-font-bold;
  position: absolute;
  z-index: $zi-highest;
  top: $spacer / 4;
  left: $spacer / 4;
  display: flex;
  padding: $spacer / 4;
  text-transform: uppercase;
  color: $secondary;
  background: $white;
  font-size: $font-size-xs;

  &::before {
    display: block;
    width: $spacer / 2;
    height: $spacer / 2;
    margin-right: $spacer / 4;
    content: "";
    border: $width-border-thin solid $gray-300;
    background: rgba($primary, $opacity-near-visible);
  }
}

.list-enter-active,
.list-leave-active {
  transition: opacity $transition-duration;
}

.list-enter,
.list-leave-to {
  opacity: 0;
}
</style>
